import Loader from 'modules/common/components/Loader';
import Modal from 'modules/common/components/Modal';
import discountsStrings from '../../strings';
import type { DiscountProduct } from '../../types/discount.types';
import { productSelectionKey } from '../../utils/discountHelpers';
import { useProductsSearch } from './useProductsSearch';
import {
  ModalBodyContent,
  ModalInput,
  ProductSearchResultItem,
  ProductSearchResultMain,
  ProductSearchResultMeta,
  ProductSearchResultPrice,
  ProductSearchResults,
  ProductSearchScrollSentinel,
  SearchResultsHeader,
  SelectProductsLoaderWrap,
  SelectProductsResultsPanel,
  SelectProductsSearchField,
  SelectionToggle,
} from '../../views/Discounts.style';

interface SelectProductsModalProps {
  isOpen: boolean;
  stacked?: boolean;
  selectedProducts: DiscountProduct[];
  onToggleProduct: (product: DiscountProduct) => void;
  onClose: () => void;
  onSave: () => void;
  productType?: number;
  offerEligibleOnly?: boolean;
  title?: string;
  searchPlaceholder?: string;
}

const SelectProductsModal = ({
  isOpen,
  stacked = false,
  selectedProducts,
  onToggleProduct,
  onClose,
  onSave,
  productType,
  offerEligibleOnly,
  title = discountsStrings.selectProductsTitle,
  searchPlaceholder = discountsStrings.searchProductsPlaceholder,
}: SelectProductsModalProps) => {
  const {
    searchQuery,
    setSearchQuery,
    allSearchResults,
    hasMore,
    isLoadingSearch,
    isLoadingMore,
    scrollContainerRef,
    loadMoreSentinelRef,
  } = useProductsSearch({ isOpen, productType, offerEligibleOnly });

  const selectedKeys = new Set(
    selectedProducts.map(p => productSelectionKey(p.productKey, p.productShapeId)),
  );

  return (
    <Modal
      toggle={isOpen}
      title={title}
      onClose={onClose}
      onCancelHandler={onClose}
      cancelActionLabel={discountsStrings.cancelBtn}
      onConfirmHandler={onSave}
      confirmActionLabel={discountsStrings.selectBtn}
      disableConfirmButton={selectedProducts.length === 0}
      closeOnClickOutside={false}
      size="md"
      extendStyle={stacked ? 'z-index: 1000;' : undefined}
    >
      <ModalBodyContent $fixedHeight>
        <SelectProductsSearchField>
          <ModalInput
            value={searchQuery}
            onChange={event => setSearchQuery(event.target.value)}
            placeholder={searchPlaceholder}
          />
        </SelectProductsSearchField>

        <SearchResultsHeader>
          <span>{discountsStrings.searchResultsLabel}</span>
          <span>{discountsStrings.productsAddedLabel(selectedProducts.length)}</span>
        </SearchResultsHeader>

        <SelectProductsResultsPanel>
          <ProductSearchResults ref={scrollContainerRef}>
            {isLoadingSearch && allSearchResults.length === 0 ? (
              <SelectProductsLoaderWrap>
                <Loader />
              </SelectProductsLoaderWrap>
            ) : (
              <>
                {allSearchResults.map(product => {
                  const key = productSelectionKey(product.productKey, product.productShapeId);
                  const selected = selectedKeys.has(key);
                  return (
                    <ProductSearchResultItem
                      key={key}
                      role="button"
                      tabIndex={0}
                      $selected={selected}
                      onClick={() => onToggleProduct(product)}
                      onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onToggleProduct(product);
                        }
                      }}
                    >
                      <ProductSearchResultMain>
                        {product.productImageUrl ? (
                          <img src={product.productImageUrl} alt={product.productName} width={40} height={40} />
                        ) : null}
                        <ProductSearchResultMeta>
                          <strong>{product.productName}</strong>
                          <ProductSearchResultPrice>
                            {`${product.originalPrice.toFixed(2)} ${discountsStrings.currency}`}
                          </ProductSearchResultPrice>
                        </ProductSearchResultMeta>
                      </ProductSearchResultMain>
                      <SelectionToggle $selected={selected}>{selected ? '✓' : '+'}</SelectionToggle>
                    </ProductSearchResultItem>
                  );
                })}
                {hasMore ? <ProductSearchScrollSentinel ref={loadMoreSentinelRef} /> : null}
                {isLoadingMore ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '12px' }}>
                    <Loader width={28} height={28} thickness={3} />
                  </div>
                ) : null}
              </>
            )}
          </ProductSearchResults>
        </SelectProductsResultsPanel>
      </ModalBodyContent>
    </Modal>
  );
};

export default SelectProductsModal;
